import { PaymentRequestTokenEvent } from '@stripe/stripe-js';
import {
  Address,
  PaymentDetails,
  StripePaymentDetails,
} from 'components/Checkout/types';
import Cookies from 'js-cookie';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { CookieNames } from 'utils/constants/cookies';
import { SHIPPING_OPTIONS, SHIPPING_TYPE } from 'utils/constants/shipping';
import { tracker } from 'utils/trackers';
import { v4 as uuidv4 } from 'uuid';

import { Order } from '../types';
import { LineItem } from '../types/orderTypes';
import { trackJustUno } from '../vendor/justuno';
import { StoreApi as api } from './instances';
import { getNYProducts } from './productsApi';
import { getUserSession } from './sessionApis';

export const createOrder = (
  data:
    | {
        user_id: number;
      }
    | { token: string },
) => api.post<Order>('/v2/orders/', data);

export const getOrder = (orderId: number) =>
  api.get<Order>(`/v2/orders/${orderId}`);

export const addLineItem = async (data: {
  lineItem: {
    variant_id: number;
    quantity: number;
  };
  orderId: number;
}) => {
  // eventId is used to tie the request and GTM event information
  const eventId = uuidv4();

  // do we still need to send the userIp? https://github.com/EverlyWell/www/pull/1100
  // const userIp = await getUserIp();

  const response = await api.post<LineItem>(
    `/v1/orders/${data.orderId}/line_items`,
    {
      ...data.lineItem,
    },
  );

  try {
    /* successful add to cart tracking */
    trackJustUno('cartItemAdd', {
      productid: response.data.product_id,
      variationid: response.data.variant_id,
      quantity: data.lineItem.quantity,
      price: response.data.amount,
    });

    // get the order to pass to the tracker
    const order = await getOrder(data.orderId).then((res) => res.data);

    tracker.trackAddItemToCart(
      {
        currencyCode: 'USD',
        productId: response.data.product_id,
        productName: response.data.product_name,
        productPrice: response.data.total,
        productSubscription: 'false',
        productQuantity: data.lineItem.quantity,
        productVariantId: response.data.variant_id,
        eventId: eventId.toString(),
      },
      order,
    );
  } catch (e) {}
  return response;
};

export const updateLineItemQuantity = async (data: {
  variantId: number;
  quantity: number;
  orderId: number;
}) =>
  api.post<LineItem>(`/v1/orders/${data.orderId}/line_items`, {
    variant_id: data.variantId,
    quantity: data.quantity,
    total_quantity: data.quantity,
  });

export const removeLineItem = async (data: {
  orderId: number;
  lineItemId: number;
}) => api.delete(`/v1/orders/${data.orderId}/line_items/${data.lineItemId}`);

export type ApplyPromoCodePayload = { code: string; orderId: number };

export const applyPromoCode = async (data: ApplyPromoCodePayload) =>
  api.put<Order>(`/v2/orders/${data.orderId}/add_promo`, {
    coupon_code: data.code,
  });

export type RemoveAllPromosPayload = { orderId: number };

export const removeAllPromos = async (data: RemoveAllPromosPayload) =>
  api.put<Order>(`/v2/orders/${data.orderId}/remove_promos`);

export type QuoteTaxPayload = {
  orderId: number;
  address: {
    zipcode: string;
  };
};

export type QuoteTaxResponse = {
  total_tax: number;
};

/**
 * Quote tax for an order
 * @param payload QuoteTaxPayload
 * @returns AxiosResponse<QuoteTaxResponse>
 */
export const quoteTax = async (payload: QuoteTaxPayload) =>
  api.post<QuoteTaxResponse>(`/v2/orders/${payload.orderId}/quote_tax`, {
    addresses: {
      ...payload.address,
    },
  });

export type ProcessOrderAddress = {
  firstName: string;
  lastName: string;
  phone: string | undefined;
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  state: string;
};

export type ProcessOrderPayload = {
  orderId: number;
  data: {
    email: string;
    billingAddress?: ProcessOrderAddress;
    shippingAddress: ProcessOrderAddress;
    emailOptIn: boolean;
    smsOptIn: boolean;
    cookieData: {
      ipCookie: string | null | undefined;
      campaignId: string | null | undefined;
      templateId: string | null | undefined;
      messageId: string | null | undefined;
    };
    shippingMethodId: string | number;
    attribution: string | null | undefined; // How did you hear about us? (Optional). This is de
    payment: PaymentDetails | null;
  };
};

export type ProcessOrderResponse = any;

/**
 * Process order from Checkout page with specified payment method
 *
 * @param order Order Object
 * @param checkoutData Data from the checkout Form or payment service
 * @param paymentDetails Object containing payment-method-specific details
 *
 * @returns Promise that will resolve with a completed Order object
 */
export const processOrder = async (payload: ProcessOrderPayload) =>
  api.put<ProcessOrderResponse>(`/v2/orders/${payload.orderId}/process_order`, {
    synchronous: 'true', // This is a synchronous request
    // Ref: src/components/Checkout/checkoutHelpers.ts#processOrder
    // Billing agreement id is added here to match the old checkout process
    billing_agreement_id: payload.data.payment?.billing_agreement_id,
    email: payload.data.email,
    terms: '1',
    marketing: payload.data.emailOptIn ? '1' : '0',
    shipState: payload.data.shippingAddress.state,
    billState: payload.data.billingAddress?.state,
    ship_address: {
      country: 'US',
      type: 'shipping',
      city: payload.data.shippingAddress.city,
      first_name: payload.data.shippingAddress.firstName,
      last_name: payload.data.shippingAddress.lastName,
      phone: payload.data.shippingAddress.phone,
      state: payload.data.shippingAddress.state,
      street1: payload.data.shippingAddress.address1,
      street2: payload.data.shippingAddress.address2,
      zipcode: payload.data.shippingAddress.zipCode,
    },
    smsOptIn: payload.data.smsOptIn,
    bill_address: {
      country: 'US',
      type: 'billing',
      city: payload.data.billingAddress?.city,
      first_name: payload.data.billingAddress?.firstName,
      last_name: payload.data.billingAddress?.lastName,
      phone: payload.data.billingAddress?.phone,
      state: payload.data.billingAddress?.state,
      street1: payload.data.billingAddress?.address1,
      street2: payload.data.billingAddress?.address2,
      zipcode: payload.data.billingAddress?.zipCode,
    },

    cookie_data: {
      ipCookie: payload.data.cookieData.ipCookie,
      campaignId: payload.data.cookieData.campaignId,
      templateId: payload.data.cookieData.templateId,
      messageId: payload.data.cookieData.messageId,
    },
    payment: payload.data.payment,
    shipping_method_id: payload.data.shippingMethodId,
    attribution: payload.data.attribution,
  });

export type CompleteOrderPayload = ProcessOrderPayload;

export type CompleteOrderResponse = any;

export const completeOrder = async (payload: CompleteOrderPayload) =>
  api.put<CompleteOrderResponse>(`/v2/orders/${payload.orderId}/complete`, {
    email: payload.data.email,
    terms: '1',
    marketing: payload.data.emailOptIn ? '1' : '0',
    shipState: payload.data.shippingAddress.state,
    billState: payload.data.billingAddress?.state,
    ship_address: {
      country: 'US',
      type: 'shipping',
      city: payload.data.shippingAddress.city,
      first_name: payload.data.shippingAddress.firstName,
      last_name: payload.data.shippingAddress.lastName,
      phone: payload.data.shippingAddress.phone,
      state: payload.data.shippingAddress.state,
      street1: payload.data.shippingAddress.address1,
      street2: payload.data.shippingAddress.address2,
      zipcode: payload.data.shippingAddress.zipCode,
    },
    smsOptIn: payload.data.smsOptIn,
    bill_address: {
      country: 'US',
      type: 'billing',
      city: payload.data.billingAddress?.city,
      first_name: payload.data.billingAddress?.firstName,
      last_name: payload.data.billingAddress?.lastName,
      phone: payload.data.billingAddress?.phone,
      state: payload.data.billingAddress?.state,
      street1: payload.data.billingAddress?.address1,
      street2: payload.data.billingAddress?.address2,
      zipcode: payload.data.billingAddress?.zipCode,
    },

    cookie_data: {
      ipCookie: payload.data.cookieData.ipCookie,
      campaignId: payload.data.cookieData.campaignId,
      templateId: payload.data.cookieData.templateId,
      messageId: payload.data.cookieData.messageId,
    },

    shipping_method_id: payload.data.shippingMethodId,
    attribution: payload.data.attribution,
    ...payload.data.payment,
  });

export type ProcessExpressPayOrderPayload =
  | {
      type: 'singleItem';
      variantId: number;
      promoCode?: string;
      data: Omit<PaymentRequestTokenEvent, 'complete' | 'token'>;
      token: PaymentRequestTokenEvent['token'];
    }
  | {
      type: 'entireOrder';
      order: Order;
      data: Omit<PaymentRequestTokenEvent, 'complete' | 'token'>;
      token: PaymentRequestTokenEvent['token'];
    };

export const processExpressPayOrder = async (
  payload: ProcessExpressPayOrderPayload,
) => {
  const { data, token } = payload;

  let order: Order;
  if (payload.type === 'singleItem') {
    const { variantId, promoCode } = payload;

    // get session
    const sessionResponse = await getUserSession();
    const session = sessionResponse.data;

    const { token: userToken, user_id: userId } = session;

    // create order
    const orderResponse = userId
      ? await createOrder({ user_id: userId })
      : await createOrder({ token: userToken });
    order = { ...orderResponse.data };

    // add item to cart
    const lineItem = await addLineItem({
      lineItem: {
        variant_id: variantId,
        quantity: 1,
      },
      orderId: order.id,
    });
    order.line_items = [lineItem.data];

    // add promo is applicable
    if (promoCode) {
      const promoOrderResponse = await applyPromoCode({
        code: promoCode,
        orderId: order.id,
      });
      order = promoOrderResponse.data;
    }
  } else if (payload.type === 'entireOrder') {
    order = payload.order;
  } else {
    throw new Error(
      'Unable to process your order. Please try again or reach out to support.',
    );
  }

  // build order payload
  const { payerEmail, shippingAddress } = data;
  const { card, id } = token;
  if (payerEmail && shippingAddress && card && id) {
    const { addressLine, city, country, postalCode, region, recipient } =
      shippingAddress;

    const {
      name,
      address_city,
      address_country,
      address_zip,
      address_state,
      address_line1,
      address_line2,
    } = card;

    const splitShipName = (recipient ?? '').split(' ');

    const shipping = {
      firstName: splitShipName.slice(0, -1).join(' '),
      lastName: splitShipName[splitShipName.length - 1],
      address1: addressLine?.[0] ?? '',
      address2: addressLine?.[1] ?? '',
      zipCode: postalCode ?? '',
      state: region ?? '',
      city: city ?? '',
      country: (country ?? '').toLocaleUpperCase(),
      type: 'shipping',
      phone: undefined,
    };

    const splitBillName = (name ?? '').split(' ');

    const billing = {
      firstName: splitBillName.slice(0, -1).join(' '),
      lastName: splitBillName[splitBillName.length - 1],
      address1: address_line1 ?? '',
      address2: address_line2 ?? '',
      zipCode: address_zip ?? '',
      state: address_state ?? '',
      city: address_city ?? '',
      country: (address_country ?? '').toLocaleUpperCase(),
      type: 'billing',
      phone: undefined,
    };

    // Build cookie metadata as parameters for iterable
    const ipCookie = Cookies.get(CookieNames.UserIp);
    const campaignId = Cookies.get(CookieNames.IterableEmailCampaignId);
    const templateId = Cookies.get(CookieNames.IterableTemplateId);
    const messageId = Cookies.get(CookieNames.IterableMessageId);
    const cookieMetaData = {
      ipCookie,
      campaignId,
      templateId,
      messageId,
    };

    // Check if order is allowed in NY
    const restrictedStates = ['NY'];
    const isOrderAllowedInNY = await isOrderAllowedInNewYork(order);

    if (restrictedStates.indexOf(region ?? '') !== -1 && !isOrderAllowedInNY) {
      analytics.track({
        event: ANALYTICS.EVENTS.RESTRICTED_STATE_ERROR,
        data: {
          label: 'DLS Cart Restricted State Error',
          id: order.id,
          revenue: order.total,
        },
      });
      throw new Error(
        `For regulatory reasons, our tests are not permitted for use in NY at this time.`,
      );
    }

    // assemble order data
    const orderData: ProcessOrderPayload = {
      orderId: order.id,
      data: {
        email: payerEmail.trim(),
        emailOptIn: false,
        smsOptIn: false,
        payment: {
          stripe_token: id,
          payment_method: 'stripe',
        } as StripePaymentDetails,
        shippingAddress: shipping,
        billingAddress: billing,
        cookieData: cookieMetaData,
        shippingMethodId: SHIPPING_TYPE.STANDARD, // free standard shipping
        attribution: null,
      },
    };

    // process order
    const processOrderResponse = await processOrder(orderData);

    // track purchase
    tracker.trackEntirePurchase({
      order,
      marketingData: {
        firstName: shipping.firstName,
        lastName: shipping.lastName,
        city: shipping.city,
        state: shipping.state,
        phone: shipping.phone ?? '',
        email: orderData.data.email,
        emailOptIn: false,
      },
      shippingInfo: {
        fee: SHIPPING_OPTIONS[SHIPPING_TYPE.STANDARD].fee,
        id: SHIPPING_TYPE.STANDARD.toString(),
        name: SHIPPING_OPTIONS[SHIPPING_TYPE.STANDARD].name,
      },
      paymentMethod: 'expresspay',
    });

    return processOrderResponse;
  } else {
    throw new Error('Invalid data for express pay order');
  }
};

const isOrderAllowedInNewYork = async (
  order: Order | undefined,
): Promise<boolean> => {
  if (!order) {
    return false;
  }

  const nyProducts = await getNYProducts();
  const nyProductsSkus = nyProducts.map((nyProduct) => nyProduct.sku);

  const lineItems = order.line_items;

  const isNotAllowed = lineItems.some((item) => {
    if (
      !nyProductsSkus.includes(item.variant_sku ?? '') &&
      !nyProductsSkus.includes(item.product_sku ?? '')
    ) {
      return true;
    }
    return false;
  });

  return !isNotAllowed;
};

export type AddShippingPayload = {
  orderId: number;
  data: {
    shippingMethodId: string;
  };
};

export type AddShippingResponse = unknown;

export const addShipping = async (payload: AddShippingPayload) =>
  api.put<CompleteOrderResponse>(`/v2/orders/${payload.orderId}/add_shipping`, {
    shipping_method_id: payload.data.shippingMethodId,
  });

export type GetPaymentIntentPayload = {
  orderId: number;
};

export type GetPaymentIntentResponse = {
  payment_intent: {
    client_secret: string;
  };
};
/**
 * Get a payment intent for an order
 *
 * @param payload
 * @returns
 */
export const getPaymentIntent = async (payload: GetPaymentIntentPayload) =>
  api.post<GetPaymentIntentResponse>(
    `/v2/orders/${payload.orderId}/payment_intent`,
  );

/**
 * Force to get a new payment intent and update the order to in_progress status
 *
 * @param payload
 * @returns
 */
export const reissuePaymentIntent = async (payload: GetPaymentIntentPayload) =>
  api.post<GetPaymentIntentResponse>(
    `/v2/orders/${payload.orderId}/payment_intent?reissue_intent=true`,
  );

export type GetOrderAddressPayload = {
  orderId: number;
  addressId: number | string;
};

export type GetOrderAddressResponse = Address;

/**
 * Get the shipping or billing address info for an order from Store API
 *
 * @param orderId number
 * @param addressId number
 * @returns Promise resolving to the address as it exists in the database
 */
export const getOrderAddress = async (payload: GetOrderAddressPayload) =>
  api.get<GetOrderAddressResponse>(
    `/v2/orders/${payload.orderId}/addresses/${payload.addressId}`,
  );
